import { useEffect, useRef, useState } from 'react';
import Modal from '../components/Modal.tsx';
import { api } from '../lib/api.ts';
import UploadSlidePane from './UploadSlidePane.tsx';
import SlidePreviewModal from '../components/SlidePreviewModal.tsx';
// GALLERY_URL, warmGallery and the loader moved to lib/gallery.ts.
import { GALLERY_URL, useTemplateGallery } from '../lib/gallery.ts';
import type { LibrarySectionPick, TemplatePick, UploadedSlide } from '../editor/useDeckEditor.ts';
import type { TemplateSummary, TemplatesPayload } from '../../shared/api.ts';
import { ChevronLeft, X } from '../lib/icons.tsx';

/**
 * What the gallery iframe posts. Every arm is data the workbench child built,
 * so the guard below checks only that a `type` is there — the branches read
 * their own fields, exactly as the untyped version did.
 */
type GalleryMessage =
  | { type: 'studio-gallery-close' }
  | { type: 'studio-gallery-zoom'; slug: string; slideIdx: number; title?: string }
  | { type: 'studio-gallery-pick-batch'; picks?: TemplatePick[] }
  | { type: 'studio-gallery-pick-section'; itemSlug: string; sectionName?: string; count?: number }
  | ({ type: 'studio-gallery-pick' } & TemplatePick);

const isGalleryMessage = (d: unknown): d is GalleryMessage =>
  !!d && typeof d === 'object' && typeof (d as { type?: unknown }).type === 'string';

/** The tile the user asked to see properly. */
type ZoomTarget = { slug: string; slideIdx: number; title?: string };

type AddSlideChooserProps = {
  /** Suffixed to the dialog title ("Add a slide after 04"), so it is prose,
   *  not a position the chooser acts on — placement is settled by the caller. */
  where?: string;
  /** The deck being edited, handed to the gallery so its "Other decks" tab can
   *  exclude it. Absent in the library, which edits an item, not a deck. */
  deckSlug?: string | null;
  onDescribe?: (() => void) | null;
  /** The describe room is open on top of this one — suppresses this modal's
   *  own Escape handler so the two dialogs don't both close. */
  describeOpen?: boolean;
  onPickTemplate: (pick: TemplatePick) => void;
  onPickBatch?: ((picks: TemplatePick[]) => void) | null;
  onPickSection?: ((pick: LibrarySectionPick) => void) | null;
  onUploadSlide?: ((slide: UploadedSlide) => void) | null;
  onClose: () => void;
  busy?: boolean;
};

/**
 * "Add a slide" chooser — opened by the filmstrip's + buttons and the tile
 * menu's add-before/after. Two paths out: "Describe it in chat" (seeds the
 * command bar — the generative default) or pick a ready-made slide by sight
 * from the visual gallery, which the server inserts verbatim with no LLM
 * involved (POST /slide/from-template). In the gallery, click SELECTS and
 * only an explicit Add button inserts; "Add N slides" batches the selection
 * into one insert. Gallery picks arrive from the iframe via
 * postMessage ({ pathTail, title, variant }); if the gallery child can't
 * start, the chooser degrades to the old text list from /api/templates (the
 * same live scan).
 *
 * The new slide always lands directly AFTER the slide the + belongs to. There
 * used to be a Before/After toggle here; nothing in the studio ever opened the
 * chooser asking for "before", so the pills existed only to undo the meaning of
 * the button just pressed — a decision taken before you can see the result,
 * when dragging in the filmstrip or the overview settles it after.
 */
export default function AddSlideChooser({
  where,
  deckSlug,
  onDescribe,
  describeOpen,
  onPickTemplate,
  onPickBatch,
  onPickSection,
  onUploadSlide,
  onClose,
  busy,
}: AddSlideChooserProps) {
  // 'loading' → probing/warming the gallery child; 'ready' → iframe;
  // The gallery's three-state loader — shared with ChangeTemplateChooser.
  const {
    state: galleryState,
    painted: galleryPainted,
    onPainted,
    onFailed,
    templates,
  } = useTemplateGallery<TemplateSummary>(() =>
    api.get<Partial<TemplatesPayload>>('/api/templates').then((d) => d.templates || []),
  );
  // The upload view swaps the gallery pane for the finished-slide upload pane
  // (expert path; only when onUploadSlide given).
  const [view, setView] = useState<'gallery' | 'upload'>('gallery');
  const [zoom, setZoom] = useState<ZoomTarget | null>(null);
  const describeRef = useRef<HTMLButtonElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Picks arrive from the gallery iframe. Same-origin + same-frame only.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg: unknown = e.data;
      if (!isGalleryMessage(msg)) return;
      // Esc pressed while focus is inside the iframe — keydown never reaches
      // this document, so the gallery relays it.
      if (msg.type === 'studio-gallery-close') {
        onClose();
        return;
      }
      // "Take a closer look" on a tile. The gallery can't open this itself —
      // it's an iframe inside this modal, so its own modal would be clipped to
      // it. Deliberately NOT gated on `busy`: reading a slide is safe mid-insert.
      if (msg.type === 'studio-gallery-zoom') {
        setZoom({ slug: msg.slug, slideIdx: msg.slideIdx, title: msg.title });
        return;
      }
      if (busy) return;
      // "Add N selected" — several ticked tiles batched into one insert (one
      // undo). Each pick is a template ({ pathTail, variant }), a bank slide
      // ({ bank, slideId }) or another deck's slide ({ fromSlug, slideId }) —
      // the same shapes the single pick uses, and they can be mixed.
      if (msg.type === 'studio-gallery-pick-batch') {
        const picks = Array.isArray(msg.picks) ? msg.picks : [];
        if (picks.length > 0) onPickBatch?.(picks);
        return;
      }
      // "Add all N slides" on a library item — a suite insert, copied into the
      // deck as whole new sections (POST /section/from-library).
      if (msg.type === 'studio-gallery-pick-section') {
        onPickSection?.({ itemSlug: msg.itemSlug, sectionName: msg.sectionName, count: msg.count });
        return;
      }
      if (msg.type !== 'studio-gallery-pick') return;
      // Two pick shapes: a template ({ pathTail, variant }), or a slide copied
      // from another deck ({ fromSlug, slideId }) — the library tab included,
      // since an item owns a deck. Routed to from-template / from-deck.
      onPickTemplate({
        pathTail: msg.pathTail,
        fromSlug: msg.fromSlug || null,
        slideId: msg.slideId || null,
        title: msg.title,
        variant: msg.variant || null,
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onPickTemplate, onPickBatch, onPickSection, onClose, busy]);

  useEffect(() => {
    describeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      // The zoom modal owns Esc while it's open (it closes itself), or closing
      // it would also close the chooser underneath. Same for the describe
      // room, which opens OVER this one: its Cancel is meant to come BACK to
      // the gallery, so an Escape that closed both would defeat the point.
      if (e.key === 'Escape' && !zoom && !describeOpen) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, zoom, describeOpen]);

  const theme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';

  return (
    <>
      <Modal ariaLabel="Add a slide" onClose={onClose} className="modal--add-slide">
        <header className="modal__header modal__header--add-slide">
          <h3 className="modal__title modal__title--sm" id="add-slide-title">
            Add a slide{where ? ' ' + where : ''}
          </h3>
          <span className="add-slide__spacer" />
          <span className="add-slide__hint muted small">Click selects · Add button inserts · no AI</span>
          {onUploadSlide && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setView(view === 'upload' ? 'gallery' : 'upload')}
              disabled={busy}
            >
              {view === 'upload' ? (
                <>
                  <ChevronLeft /> Back to gallery
                </>
              ) : (
                'Upload a finished slide…'
              )}
            </button>
          )}
          {onDescribe && (
            <button type="button" className="btn btn--primary" ref={describeRef} onClick={onDescribe} disabled={busy}>
              Describe it in chat
            </button>
          )}
          <button type="button" className="iconbtn" onClick={onClose} title="Close" aria-label="Close">
            <X />
          </button>
        </header>
        {view === 'upload' && onUploadSlide && <UploadSlidePane onUpload={onUploadSlide} busy={busy} />}
        {view !== 'upload' && (galleryState === 'loading' || (galleryState === 'ready' && !galleryPainted)) && (
          <div className="add-slide__skeleton" aria-hidden="true">
            {Array.from({ length: 12 }, (_, i) => (
              <span key={i} className="add-slide__skeleton-tile" />
            ))}
          </div>
        )}
        {view !== 'upload' && galleryState === 'ready' && (
          /* The skeleton stays UP until the iframe fires `load` — it used to
             end when the HTTP probe resolved, which is the moment the
             expensive part BEGINS: Vite then has to transform the gallery's
             eager glob of every template, each pulling in the whole primitives
             module. So the first open of "+ Slide" showed the frame's bare
             --panel-2 grey for seconds, and looked broken. Hidden rather than
             unmounted, so it is loading behind the skeleton. */
          <div className={'add-slide__gallery' + (galleryPainted ? '' : ' add-slide__gallery--hidden')}>
            <iframe
              ref={iframeRef}
              className="add-slide__gallery-frame"
              src={`${GALLERY_URL}?theme=${theme}${deckSlug ? `&deck=${encodeURIComponent(deckSlug)}` : ''}`}
              title="Ready-made slides"
              onLoad={onPainted}
              onError={onFailed}
            />
            {busy && <div className="add-slide__gallery-busy" aria-hidden="true" />}
          </div>
        )}
        {view !== 'upload' && galleryState === 'unavailable' && (
          <div className="add-slide__list">
            {templates === null && <div className="muted small">Loading…</div>}
            {templates !== null && templates.length === 0 && (
              <div className="muted small">No ready-made slides available.</div>
            )}
            {(templates || []).map((t) => (
              <button
                key={t.componentName}
                type="button"
                className="add-slide__tpl"
                onClick={() => onPickTemplate(t)}
                disabled={busy}
              >
                <span className="add-slide__tpl-title">{t.title}</span>
                {t.description && <span className="add-slide__tpl-desc">{t.description}</span>}
              </button>
            ))}
          </div>
        )}
      </Modal>
      {/* A SIBLING of the dialog, not a child. `.modal` animates in with a
          transform, and a transformed ancestor is a containing block for
          `position: fixed`, so a viewer opened during those 220ms would be
          positioned against the dialog rather than the window. */}
      {zoom && (
        <SlidePreviewModal slug={zoom.slug} slideIdx={zoom.slideIdx} title={zoom.title} onClose={() => setZoom(null)} />
      )}
    </>
  );
}
